import { DomainEventBase, DomainEventMetadata, ID } from '../types/common';
import { Prompt } from '../models/prompt';
import { Folder } from '../models/folder';

// Event Types
export type PromptEvent =
  | PromptCreatedEvent
  | PromptUpdatedEvent
  | PromptDeletedEvent
  | PromptMovedEvent;

export type FolderEvent =
  | FolderCreatedEvent
  | FolderUpdatedEvent
  | FolderDeletedEvent
  | FolderMovedEvent;

export type DomainEvent = PromptEvent | FolderEvent;

// Prompt Events
export interface PromptCreatedEvent extends DomainEventBase {
  type: 'prompt.created';
  payload: {
    prompt: Prompt;
  };
}

export interface PromptUpdatedEvent extends DomainEventBase {
  type: 'prompt.updated';
  payload: {
    promptId: ID;
    changes: Partial<Prompt>;
    previousVersion: Prompt;
  };
}

export interface PromptDeletedEvent extends DomainEventBase {
  type: 'prompt.deleted';
  payload: {
    promptId: ID;
    folderId: ID;
  };
}

export interface PromptMovedEvent extends DomainEventBase {
  type: 'prompt.moved';
  payload: {
    promptId: ID;
    fromFolderId: ID;
    toFolderId: ID;
  };
}

// Folder Events
export interface FolderCreatedEvent extends DomainEventBase {
  type: 'folder.created';
  payload: {
    folder: Folder;
  };
}

export interface FolderUpdatedEvent extends DomainEventBase {
  type: 'folder.updated';
  payload: {
    folderId: ID;
    changes: Partial<Folder>;
    previousVersion: Folder;
  };
}

export interface FolderDeletedEvent extends DomainEventBase {
  type: 'folder.deleted';
  payload: {
    folderId: ID;
    parentId?: ID;
    // Add a type field to match the prompt.deleted event structure
    type: 'folder_deleted';
  };
}

export interface FolderMovedEvent extends DomainEventBase {
  type: 'folder.moved';
  payload: {
    folderId: ID;
    fromParentId?: ID;
    toParentId?: ID;
  };
}

// Event Publisher Interface
export interface DomainEventPublisher {
  publish<T extends DomainEvent>(event: T): Promise<void>;
  subscribe<T extends DomainEvent['type']>(
    eventType: T,
    handler: (event: Extract<DomainEvent, { type: T }>) => Promise<void>
  ): () => void;
}

// Event Factory
export class DomainEventFactory {
  static createEvent<T extends DomainEvent>(
    type: T['type'],
    payload: T['payload'],
    metadata: Partial<DomainEventMetadata> = {}
  ): T {
    return {
      type,
      payload,
      metadata: {
        timestamp: Date.now(),
        ...metadata,
      },
    } as T;
  }

  // Prompt Event Factories
  static promptCreated(prompt: Prompt, userId?: ID): PromptCreatedEvent {
    return this.createEvent('prompt.created', { prompt }, { userId });
  }

  static promptUpdated(
    promptId: ID,
    changes: Partial<Prompt>,
    previousVersion: Prompt,
    userId?: ID
  ): PromptUpdatedEvent {
    return this.createEvent('prompt.updated', { promptId, changes, previousVersion }, { userId });
  }

  static promptDeleted(promptId: ID, folderId: ID, userId?: ID): PromptDeletedEvent {
    return this.createEvent('prompt.deleted', { promptId, folderId }, { userId });
  }

  static promptMoved(
    promptId: ID,
    fromFolderId: ID,
    toFolderId: ID,
    userId?: ID
  ): PromptMovedEvent {
    return this.createEvent('prompt.moved', { promptId, fromFolderId, toFolderId }, { userId });
  }

  // Folder Event Factories
  static folderCreated(folder: Folder, userId?: ID): FolderCreatedEvent {
    return this.createEvent('folder.created', { folder }, { userId });
  }

  static folderUpdated(
    folderId: ID,
    changes: Partial<Folder>,
    previousVersion: Folder,
    userId?: ID
  ): FolderUpdatedEvent {
    return this.createEvent('folder.updated', { folderId, changes, previousVersion }, { userId });
  }

  static folderDeleted(folderId: ID, parentId?: ID, userId?: ID): FolderDeletedEvent {
    return this.createEvent(
      'folder.deleted',
      { folderId, parentId, type: 'folder_deleted' },
      { userId }
    );
  }

  static folderMoved(
    folderId: ID,
    fromParentId?: ID,
    toParentId?: ID,
    userId?: ID
  ): FolderMovedEvent {
    return this.createEvent('folder.moved', { folderId, fromParentId, toParentId }, { userId });
  }
}
